import { useState } from "react";
import {
  AlertCircle,
  Apple,
  Check,
  Code2,
  Copy,
  ExternalLink,
  Globe,
  Key,
  Layers,
  Server,
  Smartphone,
  Terminal,
} from "lucide-react";
import { copyText } from "../lib/clipboard.js";
import { actorName } from "../lib/format.js";
import { useGetSetupQuery } from "../store/api.js";
import { useToast } from "../components/Toast.js";
import { Button } from "@/components/ui/button";
import type { SetupChannel, SetupPlatformState, SetupResponse } from "../contract.js";

export function SetupPage() {
  const setup = useGetSetupQuery();
  const data = setup.data;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#101828]">Setup & Integrasi Mobile</h1>
          <p className="text-sm text-[#475467] mt-1">
            Panduan konfigurasi dan kredensial untuk menghubungkan aplikasi mobile (Digibikes & Seroja) ke server OTA ini.
          </p>
        </div>
      </div>

      {setup.isLoading ? (
        <div className="rounded-xl border border-[#EAECF0] bg-white p-12 text-center shadow-xs">
          <div className="inline-block size-8 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent mb-3" />
          <p className="text-sm text-[#667085]">Memuat konfigurasi server OTA...</p>
        </div>
      ) : setup.error ? (
        <div className="rounded-xl border border-[#FDA29B] bg-[#FEF3F2] p-6 text-[#B42318] shadow-xs">
          <div className="flex items-center gap-2 font-semibold">
            <AlertCircle className="size-5" />
            <span>Gagal memuat konfigurasi setup</span>
          </div>
          <p className="mt-1 text-xs text-[#7A271A]">Silakan muat ulang halaman atau periksa koneksi server backend.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setup.refetch()}
            className="mt-4 border-[#FDA29B] bg-white text-[#B42318] hover:bg-[#FEE4E2]"
          >
            Coba Lagi
          </Button>
        </div>
      ) : data ? (
        <SetupContent data={data} />
      ) : null}
    </div>
  );
}

function SetupContent({ data }: { data: SetupResponse }) {
  const { server, apps } = data;
  const toast = useToast();
  const [copiedUrl, setCopiedUrl] = useState(false);

  async function handleCopyBaseUrl(url: string) {
    try {
      await copyText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
      toast("Base URL disalin ke clipboard");
    } catch {
      toast("Gagal menyalin teks", "danger");
    }
  }

  return (
    <div className="space-y-6">
      {/* Server Info Card */}
      <div className="rounded-xl border border-[#EAECF0] bg-white p-6 shadow-xs">
        <div className="flex items-center justify-between pb-4 border-b border-[#EAECF0]">
          <div className="flex items-center gap-3">
            <div className="size-9 rounded-lg bg-[#EFF4FF] text-[#2563EB] flex items-center justify-center">
              <Server className="size-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#101828]">Konfigurasi Server OTA</h2>
              <p className="text-xs text-[#667085]">Informasi endpoint dan public key instans server ini</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF3] border border-[#ABEFC6] px-2.5 py-0.5 text-xs font-medium text-[#027A48]">
            <span className="size-2 rounded-full bg-[#17B26A]" />
            Online ({server.environment})
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
          {/* Base URL */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#344054]">Base URL Server</label>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-[#EAECF0] bg-[#F6F7F9] px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <Globe className="size-4 text-[#98A2B3] shrink-0" />
                <span className="font-mono text-xs text-[#101828] break-all select-all">
                  {server.publicBaseUrl}
                </span>
              </div>
              <button
                type="button"
                onClick={() => void handleCopyBaseUrl(server.publicBaseUrl)}
                className="rounded p-1 text-[#667085] hover:text-[#101828] hover:bg-[#EAECF0] shrink-0"
                title="Salin Base URL"
              >
                {copiedUrl ? <Check className="size-4 text-[#17B26A]" /> : <Copy className="size-4" />}
              </button>
            </div>
          </div>

          {/* Environment */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#344054]">Environment</label>
            <div className="flex items-center gap-2 rounded-lg border border-[#EAECF0] bg-[#F6F7F9] px-3 py-2">
              <Layers className="size-4 text-[#98A2B3] shrink-0" />
              <span className="font-medium text-xs text-[#101828] capitalize">
                {server.environment}
              </span>
            </div>
          </div>
        </div>

        {/*
          There is no server-wide manifest key any more: each channel signs with
          its own, so the key is shown inside the channel block below.
        */}
        <div className="mt-5 rounded-lg border border-[#EAECF0] bg-[#F6F7F9] p-3 flex items-start gap-2.5">
          <Key className="size-4 text-[#2563EB] shrink-0 mt-0.5" />
          <p className="text-[11px] text-[#667085] leading-normal">
            Setiap kanal punya <strong className="text-[#344054]">manifest public key</strong> sendiri, jadi satu server ini bisa
            melayani semua aplikasi dan semua environment. Kuncinya ada di blok kanal masing-masing di bawah.
          </p>
        </div>
      </div>

      {/* App & Channel Integration (.env snippets) */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-[#101828]">Variabel Lingkungan Aplikasi (.env)</h2>
          <p className="text-xs text-[#475467]">
            Salin konfigurasi berikut ke berkas .env pada repositori proyek mobile untuk masing-masing kanal rilis.
          </p>
        </div>

        {apps.length === 0 ? (
          <div className="rounded-xl border border-[#EAECF0] bg-white p-8 text-center text-[#667085]">
            Belum ada aplikasi yang terdaftar di server ini.
          </div>
        ) : (
          apps.map((app) => (
            <div key={app.slug} className="rounded-xl border border-[#EAECF0] bg-white shadow-xs overflow-hidden">
              {/* App Card Header */}
              <div className="flex items-center justify-between bg-[#F8F9FC] px-6 py-4 border-b border-[#EAECF0]">
                <div className="flex items-center gap-2.5">
                  <div className="size-7 rounded-md bg-[#EFF4FF] text-[#2563EB] font-bold flex items-center justify-center text-xs">
                    {app.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-[#101828]">{app.name}</h3>
                    <span className="font-mono text-[11px] text-[#667085]">{app.slug}</span>
                  </div>
                </div>
                <span className="text-xs text-[#667085] font-medium">
                  {app.channels.length} kanal rilis
                </span>
              </div>

              {/* Channels List */}
              <div className="p-6 space-y-6">
                {app.channels.map((ch) => (
                  <ChannelSetupBlock key={ch.name} channel={ch} appSlug={app.slug} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ChannelSetupBlock({ channel, appSlug }: { channel: SetupChannel; appSlug: string }) {
  const toast = useToast();
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const [copiedEnvFile, setCopiedEnvFile] = useState(false);

  async function handleCopySnippet() {
    try {
      await copyText(channel.snippet);
      setCopiedSnippet(true);
      setTimeout(() => setCopiedSnippet(false), 2000);
      toast(`Konfigurasi .env (${channel.name}) disalin`);
    } catch {
      toast("Gagal menyalin teks", "danger");
    }
  }

  async function handleCopyEnvFile() {
    try {
      await copyText(channel.envFile);
      setCopiedEnvFile(true);
      setTimeout(() => setCopiedEnvFile(false), 2000);
      toast("Nama berkas disalin");
    } catch {
      toast("Gagal menyalin teks", "danger");
    }
  }

  return (
    <div className="rounded-lg border border-[#EAECF0] bg-white overflow-hidden">
      {/* Channel Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-[#F6F7F9] px-4 py-2.5 border-b border-[#EAECF0]">
        <div className="flex items-center gap-2">
          <span
            className={`size-2 rounded-full ${
              channel.name === "production"
                ? "bg-[#17B26A]"
                : channel.name === "staging"
                ? "bg-[#F79009]"
                : "bg-[#854D0E]"
            }`}
          />
          <span className="font-semibold text-xs text-[#101828] capitalize">{channel.name}</span>
          <span className="text-[#D0D5DD]">·</span>
          <button
            type="button"
            onClick={() => void handleCopyEnvFile()}
            className="group flex items-center gap-1 font-mono text-xs text-[#475467] hover:text-[#101828]"
            title="Klik untuk menyalin path file"
          >
            <span>{channel.envFile}</span>
            {copiedEnvFile ? (
              <Check className="size-3 text-[#17B26A]" />
            ) : (
              <Copy className="size-3 text-[#98A2B3] group-hover:text-[#475467]" />
            )}
          </button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleCopySnippet()}
          className="h-7 text-xs bg-white text-[#344054] hover:bg-[#F6F7F9] border-[#D0D5DD]"
        >
          {copiedSnippet ? <Check className="size-3.5 mr-1 text-[#17B26A]" /> : <Copy className="size-3.5 mr-1" />}
          {copiedSnippet ? "Tersalin" : "Salin Isi .env"}
        </Button>
      </div>

      {/* Snippet Code Box */}
      <div className="p-4 bg-[#101828] text-[#F8F9FC]">
        <pre className="font-mono text-xs break-all whitespace-pre-wrap select-all leading-relaxed">
          {channel.snippet}
        </pre>
      </div>

      {/* Signing key of this channel */}
      <div className="border-t border-[#EAECF0] px-4 py-3 flex items-start gap-2.5">
        <Key className="size-4 text-[#2563EB] shrink-0 mt-0.5" />
        <div className="min-w-0 space-y-1">
          <span className="text-xs font-semibold text-[#344054]">Manifest Public Key · {channel.name}</span>
          {channel.manifestPublicKey ? (
            <pre className="font-mono text-[11px] text-[#475467] break-all whitespace-pre-wrap select-all leading-relaxed">
              {channel.manifestPublicKey}
            </pre>
          ) : (
            <p className="text-[11px] text-[#B42318]">
              Server belum punya MANIFEST_MASTER_SEED, jadi manifest kanal ini dikirim tanpa tanda tangan. Jalankan{" "}
              <code>npm run keys:generate</code> di host server lalu restart.
            </p>
          )}
        </div>
      </div>

      {/* Platform Status */}
      <div className="border-t border-[#EAECF0] p-4 bg-[#FAFAFA]">
        <h4 className="text-xs font-semibold text-[#344054] mb-3">Status Platform Terkini ({channel.name})</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[#EAECF0] text-[#667085] text-[11px] font-semibold">
                <th className="pb-2 font-medium">Platform</th>
                <th className="pb-2 font-medium">Rilis Aktif</th>
                <th className="pb-2 font-medium">Perangkat Aktif</th>
                <th className="pb-2 font-medium">Target Native</th>
                <th className="pb-2 font-medium">Fingerprint yang Dilaporkan Perangkat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {channel.platforms.map((p) => (
                <tr key={p.platform} className="hover:bg-white transition-colors">
                  <td className="py-2.5 font-medium text-[#101828]">
                    <div className="flex items-center gap-1.5 capitalize">
                      {p.platform === "android" ? (
                        <Smartphone className="size-3.5 text-[#17B26A]" />
                      ) : (
                        <Apple className="size-3.5 text-[#2563EB]" />
                      )}
                      {p.platform}
                    </div>
                  </td>
                  <td className="py-2.5 font-mono text-[#344054]">
                    {p.activeReleaseSeq !== null ? (
                      <span className="font-bold text-[#101828]">seq {p.activeReleaseSeq}</span>
                    ) : (
                      <span className="text-[#98A2B3]">—</span>
                    )}
                  </td>
                  <td className="py-2.5 font-mono text-[#344054]">
                    {p.devices.toLocaleString("id-ID")}
                  </td>
                  {/*
                    Installs on anything other than this are told to update, so
                    it is shown beside the fingerprints they actually report.
                  */}
                  <td className="py-2.5">
                    {p.target ? (
                      <div className="space-y-1 max-w-[220px]">
                        <code className="block font-mono text-[11px] text-[#101828] bg-white border border-[#EAECF0] px-1.5 py-0.5 rounded break-all select-all">
                          {p.target.fingerprint}
                        </code>
                        <span className="text-[11px] text-[#667085]">
                          {p.target.blocking ? "mengunci aplikasi" : "banner"} · {actorName(p.target.setBy)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[#98A2B3] text-[11px]">belum disetel</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    {p.fingerprints.length === 0 ? (
                      <span className="text-[#98A2B3] font-mono text-[11px]">—</span>
                    ) : (
                      <div className="space-y-1.5 max-w-lg">
                        {p.fingerprints.map((f) => (
                          <div key={f.fingerprint} className="flex flex-col sm:flex-row sm:items-center gap-1 text-[11px]">
                            <code className="font-mono text-[#101828] bg-white border border-[#EAECF0] px-1.5 py-0.5 rounded break-all select-all">
                              {f.fingerprint}
                            </code>
                            <span className="text-[#667085] shrink-0 font-medium">
                              (v{f.nativeVersion} · {f.devices} perangkat)
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
